import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  submitCorporateAndOrgStructureFailure,
  submitCorporateAndOrgStructureRequest,
} from "../../../../store/features/actions/corporateAndOrgStructureActions";
import {
  getAllSubdimensionMatches,
  getAllPageInsights,
} from "../../../../services/commonService";
import CompanyOverview from "./CompanyOverview";
import HeadquartersAndBusinessSegments from "./HeadquartersAndBusinessSegments";
import OwnershipAndStructure from "./OwnershipAndStructure";
import ExecutiveTeamAndKeyLeadership from "./ExecutiveTeamAndKeyLeadership";
import WorkforceAndHiringTrends from "./WorkforceAndHiringTrends";
import "./CorporateOrgStructure.css";
import InsightBlock from "../InsightBlock";
import GenericOverviewSideCard from "../../genericComponents/GenericOverviewSideCard";
import AIChat from "../../Chat/AiChat";
import GenericTableSkeleton from "../../skeletons/tableSkeleton/genericTableSkeleton";

function CorporateOrgStructure({
  activeSubKey,
  setActiveSubKey,
  scrollTarget,
}) {
  const insightSummaryBRef = useRef(null);
  const companyOverviewRef = useRef(null);
  const headquartersRef = useRef(null);
  const ownershipStructureRef = useRef(null);
  const executiveTeamRef = useRef(null);
  const workforceRef = useRef(null);
  const programmaticScrollRef = useRef(false);

  const { clientName, competitorName } = useParams();
  const [showConversationModal, setShowConversationModal] = useState(false);
  const [pagePositives, setPagePositives] = useState([]);
  const [pageNegatives, setPageNegatives] = useState([]);
  const [pageSoWhat, setPageSoWhat] = useState([]);
  const [companyOverviewData, setCompanyOverviewData] = useState([]);
  const [
    headquartersAndBusinessSegmentData,
    setHeadquartersAndBusinessSegmentData,
  ] = useState([]);
  const [ownershipAndStructureData, setOwnershipAndStructureData] = useState(
    []
  );
  const [workforceAndHiringTrends, setWorkforceAndHiringTrends] = useState([]);
  const [headquartersAndBusinessSegments, setHeadquartersAndBusinessSegments] =
    useState([]);
  const [executiveTeamAndKeyLeadership, setExecutiveTeamAndKeyLeadership] =
    useState([]);

  const sectionMap = {
    insightSummaryTabB: insightSummaryBRef,
    companyOverviewTab: companyOverviewRef,
    headquartersTab: headquartersRef,
    ownershipStructureTab: ownershipStructureRef,
    executiveTeamTab: executiveTeamRef,
    workforceTab: workforceRef,
  };

  useEffect(() => {
    if (!activeSubKey) return;

    const sectionRef = sectionMap[activeSubKey];
    if (sectionRef?.current) {
      // mark as programmatic scroll so scroll-spy ignores this movement
      programmaticScrollRef.current = true;

      const yOffset = -100;
      const y =
        sectionRef.current.getBoundingClientRect().top +
        window.scrollY +
        yOffset;

      window.scrollTo({ top: y, behavior: "smooth" });

      // clear the flag after animation; tune timeout to match your animation time
      const t = setTimeout(() => {
        programmaticScrollRef.current = false;
      }, 700);

      return () => clearTimeout(t);
    }
  }, [activeSubKey]);

  // +++++++++++++++++++++++++++++++++++++++++++++
  // USEEFFECT FOR UPDATING LEFT NAVIGATION LINK ON SCROLL

  useEffect(() => {
    let scrollTimeout = null;
    let ticking = false;

    const handleScroll = () => {
      if (programmaticScrollRef.current) return; // ignore programmatic scrolls
      if (ticking) return;
      ticking = true;

      window.requestAnimationFrame(() => {
        // Clear previous timeout (debounce)
        if (scrollTimeout) clearTimeout(scrollTimeout);

        // Wait until user stops scrolling for 200ms
        scrollTimeout = setTimeout(() => {
          const scrollPosition = window.scrollY + 120; // offset for header, etc.
          let currentKey = null;

          Object.entries(sectionMap).forEach(([key, ref]) => {
            if (!ref.current) return;
            const sectionTop = ref.current.offsetTop;
            const sectionHeight = ref.current.offsetHeight;

            // Check if scroll is within this section
            if (
              scrollPosition >= sectionTop &&
              scrollPosition < sectionTop + sectionHeight
            ) {
              currentKey = key;
            }
          });

          // update nav only if different
          if (currentKey && currentKey !== activeSubKey) {
            setActiveSubKey(currentKey);
          }
        }, 200); // change nav only after 200ms of no scrolling

        ticking = false;
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    // run once on mount
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollTimeout) clearTimeout(scrollTimeout);
    };
  }, [activeSubKey, setActiveSubKey]);

  // USEEFFECT FOR UPDATING LEFT NAVIGATION LINK ON SCROLL
  // +++++++++++++++++++++++++++++++++++++++++++++

  const actions = [
    // { type: "chart", title: "Chart", onClick: () => {} },
    {
      type: "conversation",
      title: "Conversation",
      onClick: () => {
        setShowConversationModal((prev) => !prev);
      },
    },
    { type: "mindMap", title: "Mind Map", onClick: () => {} },
    { type: "download", title: "Download", onClick: () => {} },
  ];

  const dispatch = useDispatch();

  const { loading, data, error } = useSelector(
    (state) => state.corporateAndOrgStructureReducer
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
  // HANDLE SCROLL FROM SEARCH (from parent via scrollTarget)
  // +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
  useEffect(() => {
    if (!scrollTarget) return;

    const targetKey = scrollTarget.key;
    const sectionRef = sectionMap[targetKey];

    const scrollToSection = () => {
      if (!sectionRef?.current) {
        // If ref not ready, try again
        requestAnimationFrame(scrollToSection);
        return;
      }

      // Mark as programmatic scroll
      programmaticScrollRef.current = true;

      const yOffset = -100;
      const element = sectionRef.current;
      const y = element.getBoundingClientRect().top + window.scrollY + yOffset;

      window.scrollTo({ top: y, behavior: "smooth" });

      // Clear flag after animation
      setTimeout(() => {
        programmaticScrollRef.current = false;
      }, 700);
    };

    // Wait for content to render if loading
    if (loading) {
      console.log(" Waiting for data to load...");
      return;
    }

    // Wait for DOM to be ready, then scroll
    const timer = setTimeout(() => {
      requestAnimationFrame(scrollToSection);
    }, 300);

    return () => clearTimeout(timer);
  }, [scrollTarget, loading]); // Add loading to dependencies
  // +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

  useEffect(() => {
    dispatch(
      submitCorporateAndOrgStructureRequest(
        "Corporate & Organizational Structure",
        clientName
      )
    );
  }, []);

  useEffect(() => {
    const tempCompData = getAllSubdimensionMatches(data, "Company Overview");
    setCompanyOverviewData(tempCompData);
    const tempHeadquarterAndBusinessSegmentData = getAllSubdimensionMatches(
      data,
      "Headquarters & Business Segments"
    );
    setHeadquartersAndBusinessSegmentData(
      tempHeadquarterAndBusinessSegmentData
    );

    const tempOwnershipAndStructuresData = getAllSubdimensionMatches(
      data,
      "Ownership & Structure"
    );
    setOwnershipAndStructureData(tempOwnershipAndStructuresData);

    const tempExecutiveTeamAndKeyLeadershipData = getAllSubdimensionMatches(
      data,
      "Executive Team & Key Leadership"
    );
    setExecutiveTeamAndKeyLeadership(tempExecutiveTeamAndKeyLeadershipData);

    const tempWorkforceAndHiringTrendsData = getAllSubdimensionMatches(
      data,
      "Workforce & Hiring Trends"
    );
    setWorkforceAndHiringTrends(tempWorkforceAndHiringTrendsData);

    const { positive, negative, sowhat } = getAllPageInsights(data);
    setPagePositives(positive || []);
    setPageNegatives(negative || []);
    setPageSoWhat(sowhat || []);
    // console.log("POSITIVES: " + JSON.stringify(positive));
  }, [data]);

  return (
    <div>
      <div className="corporate_wrapper">
        {loading ? (
          <GenericTableSkeleton colCount={3} rowCount={5} />
        ) : (
          <div className="company_overview_wrapper" ref={insightSummaryBRef}>
            <InsightBlock
              title="Corporate & Organizational Structure"
              overview={`Overview of ${
                clientName ?? "HB Fuller"
              }’s corporate ownership, key business units, and leadership — benchmarked against public peers like Henkel and Bostik.`}
              actions={actions}
              pagePositives={pagePositives}
              pageNegatives={pageNegatives}
              pageSoWhat={pageSoWhat}
            />
          </div>
        )}

        {loading ? (
          <GenericTableSkeleton colCount={3} rowCount={5} />
        ) : (
          <div ref={companyOverviewRef}>
            <CompanyOverview companyOverviewData={companyOverviewData} />
          </div>
        )}

        {loading ? (
          <GenericTableSkeleton colCount={3} rowCount={5} />
        ) : (
          <div ref={headquartersRef}>
            <HeadquartersAndBusinessSegments
              headquartersAndBusinessSegmentData={
                headquartersAndBusinessSegmentData
              }
            />
          </div>
        )}

        {loading ? (
          <GenericTableSkeleton colCount={3} rowCount={5} />
        ) : (
          <div ref={ownershipStructureRef}>
            <OwnershipAndStructure
              ownershipAndStructureData={ownershipAndStructureData}
            />
          </div>
        )}

        {loading ? (
          <GenericTableSkeleton colCount={3} rowCount={5} />
        ) : (
          <div ref={executiveTeamRef}>
            <ExecutiveTeamAndKeyLeadership
              executiveTeamAndKeyLeadership={executiveTeamAndKeyLeadership}
            />
          </div>
        )}

        {loading ? (
          <GenericTableSkeleton colCount={3} rowCount={5} />
        ) : (
          <div ref={workforceRef}>
            <WorkforceAndHiringTrends
              workforceAndHiringTrends={workforceAndHiringTrends}
            />
          </div>
        )}
      </div>

      {showConversationModal && (
        <GenericOverviewSideCard
          showMoreModal={showConversationModal}
          setShowMoreModal={setShowConversationModal}
          titleForSideCard={
            <div className="ai_insight_top_block">
              <div className="ai_insight_left">
                <div className="ai_insight_img">
                  <img
                    src="/img/ai_insight_top.png"
                    alt="icon"
                    className="ai_icon"
                  />
                </div>
                <div className="ai_insight_text">
                  <h5 className="ai_insight_top_head">
                    Decode This:{" "}
                    {competitorName ?? "Tailored Chemical Products"} - Market
                    Insights
                  </h5>
                  <p className="ai_insight_top_subhead">
                    AI-powered competitive intelligence and strategic insights
                  </p>
                </div>
              </div>
              <div className="ai_insight_right"></div>
            </div>
          }
          comp={<AIChat section={""} />}
          minWidthForDrawer="40%"
          disableXbtn={false}
        />
      )}
    </div>
  );
}

export default CorporateOrgStructure;
